import Vue from 'vue';
import Router from 'vue-router';
import _ from 'lodash';
import { getUser, authenticate } from '@/helpers';
import { getNextOnboardingStepInfo } from './helpers/onboarding';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/:catchAll(.*)',
      name: 'AllRedirect',
      component: () => import(/* webpackChunkName: "landing" */ './views/Landing'),
    },
  ],
});

const canAccess = (to, user) => to.matched.some((record) => !record.meta.accessibleRoles
  || record.meta.accessibleRoles.includes(user.role.name));


const handleUserOnboarding = (user, next) => {
  let skills = [];
  if (user.role.name === 'creative') {
    skills = _.get(user, 'profile.skillsets', []).map((o) => o.name);
  }
  if (!user.onboardingState) {
    return next('/dashboard');
  }
  const nextStep = getNextOnboardingStepInfo({
    onboardingState: Number(user.onboardingState) + 1,
    onboardingStepCompleted: user.onboardingStepCompleted,
    role: user.role.name,
    skills,
  });
  return next(nextStep.url);
};
// eslint-disable-next-line
router.beforeEach(async (to, from, next) => {
  const user = getUser();
  const creativRoutes = ['/onboarding/creative/about-you', '/onboarding/creative/field-producer-details', '/onboarding/creative/sweep-preferences', '/onboarding/creative/language-preference', '/onboarding/creative/sweep-details', '/onboarding/creative/talent-details'];
  const creativactiveRoutes = ['/onboarding/creative/pending', '/onboarding/creative/rejected'];
  const isInIframe = window !== window.parent;

  // Handle iframe authentication first
  if (isInIframe && !user) {
    // Create a promise that resolves when auth is complete
    const authPromise = new Promise((resolve) => {
      const authHandler = async (event) => {
        if (event.data.type === 'AUTH_TOKEN') {
          // Remove the listener once we get the token
          window.removeEventListener('message', authHandler);

          // Initialize auth with the token
          const initialAuthData = {
            token: event.data.token,
            timestamp: new Date(),
            isPopup: false,
            rememberMe: true
          };

          localStorage.setItem('__bs_valid', JSON.stringify(initialAuthData));
          router.app.$store.commit('user/SET_USER_TOKEN', event.data.token);

          // Fetch user details
          await router.app.$store.dispatch('user/getAuthInfo');
          resolve();
        }
      };

      window.addEventListener('message', authHandler);

      // Set a timeout in case we never receive the message
      setTimeout(() => resolve(), 5000);
    });

    // Wait for authentication to complete
    await authPromise;
    return next();
  }
  if (user === null && to.matched.some((record) => record.meta.requiresAuth)) {
    authenticate(to.path, router);
  } else if (user !== null && creativactiveRoutes.includes(to.path)) {
    const userInfo = user.userDetails;
    const userRole = userInfo.role.name;
    if (['creative'].includes(userRole) && userInfo.onboardingState <= '10') {
      return next();
    }
    const storeContext = router.app.$store;
    const responseAsUser = await storeContext.dispatch('profile/getProfile');
    return handleUserOnboarding(responseAsUser, next);
  } else if (user !== null && creativRoutes.includes(to.path)) {
    const userInfo = user.userDetails;
    const userRole = userInfo.role.name;
    const { onboardingState } = userInfo;
    if (['creative'].includes(userRole) && Number(onboardingState) >= 2 && Number(onboardingState) < 9) {
      // TODO - check if onboarding step is completed
      // find next route to navigate to
      return next();
    }
    const storeContext = router.app.$store;
    const responseAsUser = await storeContext.dispatch('profile/getProfile');
    return handleUserOnboarding(responseAsUser, next);
  } else if (user !== null && (to.path === '/dashboard')) {
    const userInfo = user.userDetails;
    const userRole = userInfo.role?.name;
    const { onboardingState } = userInfo;
    // eslint-disable-next-line eqeqeq
    if (['creative'].includes(userRole) && onboardingState == 10 && userInfo.status === '0') {
      return next('/onboarding/creative/pending');
    }
    // eslint-disable-next-line eqeqeq
    if (['creative'].includes(userRole) && onboardingState == 10 && userInfo.status === '4') {
      return next('/onboarding/creative/rejected');
    }
    if (['creative'].includes(userRole) && onboardingState && onboardingState < 10) {
      const storeContext = router.app.$store;
      const responseAsUser = await storeContext.dispatch('profile/getProfile');
      return handleUserOnboarding(responseAsUser, next);
    }
    if (['client'].includes(userRole) && onboardingState && onboardingState < 7) {
      // TODO - check if onboarding step is completed
      // find next route to navigate to
      const storeContext = router.app.$store;
      const responseAsUser = await storeContext.dispatch('profile/getProfile');
      return handleUserOnboarding(responseAsUser, next);
    }
    return next();
  } else if (user !== null && (to.path === '/' || to.path === '/register' || to.path === '/login' || to.path === '/onboarding/role' || !canAccess(to, user.userDetails))) {
    const userInfo = user.userDetails;
    const userRole = userInfo.role.name;
    if (['client', 'creative'].includes(userRole)) {
      // TODO - check if onboarding step is completed
      // find next route to navigate to
      const storeContext = router.app.$store;
      const responseAsUser = await storeContext.dispatch('profile/getProfile');
      return handleUserOnboarding(responseAsUser, next);
    } if (user !== null && ['/', '/register', '/login'].includes(to.path)) {
      return next('/dashboard');
    }
    return next();
  }
  return next();
});

export default router;
